import { TransferInIcon } from '@persona-id/design-tokens/icons';
import { sortBy } from 'lodash/fp';
import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import styled from 'styled-components';

import Form from 'dashboard/layouts/FormLayout/Form';
import { formatBillingDate } from 'dashboard/lib/utils/formatters';
import { Invoice } from 'dashboard/models/invoice';
import { showMessage } from 'dashboard/redux/actions/messages';
import { loadStripeInvoicePdfUrl } from 'dashboard/redux/thunk-actions/invoice';
import { InvoiceStatusPill } from 'dashboard/views/DashboardOrganizationBillingInvoicesView/DashboardOrganizationBillingInvoicesView';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Select, { Option } from 'design-system/components/Select';

import InvoiceFinalizedLineItemsTable from './InvoiceFinalizedLineItemsTable';

export interface DashboardOrganizationInvoiceFinalizedViewProps {
  invoices?: Invoice[];
  selectedInvoice: Invoice;
  loadStripeInvoicePdfUrl: (...args: Parameters<typeof loadStripeInvoicePdfUrl>) => Promise<string>;
}

const DashboardOrganizationInvoiceFinalizedView = ({
  invoices,
  selectedInvoice,
  loadStripeInvoicePdfUrl,
}: DashboardOrganizationInvoiceFinalizedViewProps & PropsFromRedux) => {
  const [invoice, setInvoice] = useState(selectedInvoice);

  useEffect(() => {
    setInvoice(selectedInvoice);
  }, [selectedInvoice]);

  const handleDownload = async (invoice: Invoice) => {
    const stripeInvoicePdfUrl = await loadStripeInvoicePdfUrl(invoice);
    window.open(stripeInvoicePdfUrl);
  };

  const invoiceDownloadButton = invoice.attributes['pdf-url'] && (
    <StyledButton
      id='stripe-invoice__btn-download'
      data-test='stripe-invoice__btn-download'
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleDownload(invoice);
      }}
      width='icon'
      variant='primary'
    >
      <TransferInIcon />
    </StyledButton>
  );

  const statusPill = (
    <InvoiceStatusPillContainer>
      <InvoiceStatusPill invoice={invoice} />
    </InvoiceStatusPillContainer>
  );

  const invoiceSelectionDropdown = invoices && (
    <Select
      onValueChange={(value) => {
        const invoice = invoices.find((invoice) => {
          return invoice.id === value;
        });
        if (invoice) {
          setInvoice(invoice);
        }
      }}
      value={invoice.id}
    >
      {sortBy([(inv) => inv.attributes['period-start-at']], invoices)
        .reverse()
        .map((invoice) => {
          return (
            <Option key={invoice.id} value={invoice.id}>
              {`${formatBillingDate(invoice.attributes['period-start-at'])} - ${formatBillingDate(
                invoice.attributes['period-end-at'],
              )}`}
            </Option>
          );
        })}
    </Select>
  );

  return (
    <Card>
      <Card.Body>
        <Card.Title>
          {`${formatBillingDate(invoice.attributes['period-start-at'])} - ${formatBillingDate(
            invoice.attributes['period-end-at'],
          )}`}
          {statusPill}
          {invoiceDownloadButton}
        </Card.Title>
        <Form>{invoiceSelectionDropdown}</Form>
        <InvoiceFinalizedLineItemsTable invoice={invoice} />
      </Card.Body>
    </Card>
  );
};

const InvoiceStatusPillContainer = styled('span')`
  margin-left: 10px;
`;

const StyledButton = styled(Button)`
  margin-left: 10px;
  margin-left: auto;
`;

const mapDispatchToProps = {
  showMessage,
  loadStripeInvoicePdfUrl,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardOrganizationInvoiceFinalizedView);
